import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'

import { Operator, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { AccountGroupType } from '@model/AccountGroupType'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import { TransferTypeEnum } from '@model/TransactionDto'
import { CurrencyType, WalletTypeEnum, isWalletETDType } from '@model/WalletDto'

import { Loading } from '@shared/components/Loading/Loading'

import { TransferSuccessModal } from '../../modals/TransferSuccess.modal'
import { TransferFormETD, TransferValues } from './TransferFormETD'

interface MatchProps {
  walletId?: string
  tradingAccountId?: string
}

export const WalletTransferETDPage = () => {
  const navigate = useNavigate()
  const { walletId, tradingAccountId }: MatchProps = useParams()
  const { state } = useLocation()
  const [currency] = useState<CurrencyType | undefined>(state?.currency)
  const apiClient = useApiClient(ClientApiClient)
  const [auth] = useContext(AuthSessionContext)
  const queryClient = useQueryClient()

  const transferSuccessModal = useModal(TransferSuccessModal, {
    onClose: () => navigate('/dashboard/traders-room/transaction-history'),
  })
  const [isSubmitting, setSubmitting] = useState(false)

  const { data = [], isLoading: fetchingAccounts } = useQuery({
    queryKey: queryKeys.walletExtended.transferData({ walletId, tradingAccountId, type: 'etd' }),
    queryFn: () =>
      Promise.all([
        apiClient.getWallets({
          ClientWalletType: `${auth?.id}__${WalletTypeEnum.ETD}`,
        }),
        apiClient.getTradingAccounts({
          pageSize: 150,
          search: {
            AccountGroupType: {
              value: AccountGroupType.Live,
              operator: Operator.EQUAL,
            },
            Platform: {
              value: PlatformTypeEnum.CQGCAST, // it's for ETD
              operator: Operator.EQUAL,
            },
            Status: {
              value: 1,
              operator: Operator.EQUAL,
            },
          },
        }),
      ]),
    refetchOnMount: 'always',
  })
  const [wallets = [], tradingAccounts] = data

  const processTransfer = (values: TransferValues) => {
    const { transferFrom } = values

    return transferFrom?.displayType === 'wallet'
      ? walletToTradingAccountTransfer(values)
      : tradingAccountToWalletTransfer(values)
  }

  const walletToTradingAccountTransfer = ({
    transferFrom,
    transferTo,
    fromAmount,
  }: TransferValues) => {
    return apiClient.transferWalletToTradingAccount({
      FromWalletId: transferFrom?.id || '',
      ToTaId: transferTo?.id || '',
      Amount: fromAmount ?? 0,
      clientIpAddress: '',
      referrer_url: '',
    })
  }

  const tradingAccountToWalletTransfer = ({
    transferFrom,
    transferTo,
    fromAmount,
  }: TransferValues) => {
    return apiClient.transferTradingAccountToWallet({
      ToWalletId: transferTo?.id || '',
      FromTaId: transferFrom?.id || '',
      Amount: fromAmount ?? 0,
      clientIpAddress: '',
      referrer_url: '',
    })
  }

  const handleSubmit = async (values: TransferValues) => {
    try {
      setSubmitting(true)
      const { transferFrom } = values
      const currentTransferType =
        transferFrom?.displayType === 'wallet'
          ? TransferTypeEnum.WALLET_TO_TA
          : TransferTypeEnum.TA_TO_WALLET

      const masterTransaction = await processTransfer(values)
      queryClient.invalidateQueries({ queryKey: ['wallet'] })

      transferSuccessModal.open({ masterTransaction, transferType: currentTransferType })
    } catch (e: unknown) {
      console.error(e)
    } finally {
      setSubmitting(false)
    }
  }

  const { t } = useTranslation()

  return (
    <Loading text={t('Wallet.Transferring...')} isLoading={isSubmitting} showLoadingIcon>
      <TransferFormETD
        fetchingAccounts={fetchingAccounts}
        wallets={wallets.filter((x) => isWalletETDType(x.walletType.id))}
        tradingAccounts={tradingAccounts?.items || []}
        preselectedWallet={wallets.find((w) => w.id === walletId)}
        preselectedTradingAccount={tradingAccounts?.items.find((ta) => ta.id === tradingAccountId)}
        currency={currency}
        handleFormSubmit={handleSubmit}
      />
    </Loading>
  )
}
